import { create } from 'zustand';
import { toastSink } from '../ui/Toast/toastSink';
import { uploadFile } from '../api/uploadFile';
import i18n from '../../i18n';

// frontend-restructuring Fase 3 (03.1-auth-store.md) -- reemplaza UserContext.jsx (React
// Context) para que la identidad/sesión sea de verdad selector-based: un consumidor que solo
// lee `user.name` no debe re-renderizar cuando cambia `user.status`/`currentSpace.icon`. Un
// store zustand es un singleton de módulo -- no necesita "re-proveerse" dentro de los árboles
// `createRoot()` de DoctexHtml.jsx como sí necesitaba `UserContext.Provider` (ver 03.1's §0.5).
//
// `updateUserProfile` usa `toastSink` (no `useToast()`) porque una acción de store no es un
// componente/hook -- no puede llamar hooks de React. Mismo patrón que `app/savePulse.js`.

const LOGGED_OUT_USER = {
    name: "User",
    email: "",
    image: "https://ui-avatars.com/api/?name=User&background=random&size=128",
    status: "Offline",
    isDbConnected: false,
    uid: null,
    currentSpace: null
};

// Wrap de `window.fetch` para invalidación de sesión (ver comentario original en
// UserContext.jsx, 2026-07-12): cualquier 401 en cualquier request autenticado dispara un
// evento global que `subscribeToSessionExpiry` (abajo) escucha para limpiar la sesión local.
// Guardado con un flag de idempotencia -- a diferencia del `UserProvider` de antes (un
// componente, uno por árbol React), este módulo puede llegar a evaluarse más de una vez
// (HMR de Vite, o la propia verificación manual del paso `a` de 03.1-auth-store.md, que
// importa este módulo mientras `UserContext.jsx` todavía tiene el suyo propio activo).
let fetchWrapped = false;
function wrapFetchOnce() {
    if (fetchWrapped || typeof window === 'undefined') return;
    fetchWrapped = true;
    const upstream = window.fetch;
    window.fetch = async (...args) => {
        const response = await upstream(...args);
        if (response.status === 401) {
            window.dispatchEvent(new CustomEvent('colonny:session-expired'));
        }
        return response;
    };
}
wrapFetchOnce();

let sessionExpiredListenerRegistered = false;
function registerSessionExpiredListenerOnce() {
    if (sessionExpiredListenerRegistered || typeof window === 'undefined') return;
    sessionExpiredListenerRegistered = true;
    window.addEventListener('colonny:session-expired', () => {
        useAuthStore.getState().clearSession();
    });
}

export const useAuthStore = create((set, get) => ({
    user: LOGGED_OUT_USER,
    loading: true,
    spaceCheckDone: false,

    // Resuelve `current_space` desde `/api/customs/:dbId/current_space` cuando el usuario no
    // trae uno ya resuelto (ni en localStorage ni en la respuesta de login). Se llama tanto
    // desde `bootstrap()` (montaje) como desde `finalizeLogin` (login fresco) -- 03.1's §1
    // corrigió esto: el efecto original de UserContext.jsx se re-disparaba con cualquier
    // cambio de `user.dbId`, incluido el que pone un login exitoso, así que un usuario que
    // inicia sesión sin `currentSpace` en la respuesta pero con uno persistido de otra sesión
    // lo recuperaba automáticamente -- este helper preserva ese comportamiento explícitamente.
    resolveCurrentSpace: async (dbId) => {
        if (!dbId) {
            set({ spaceCheckDone: true });
            return;
        }
        if (get().user.currentSpace) {
            set({ spaceCheckDone: true });
            return;
        }
        try {
            const res = await fetch(`/api/customs/${dbId}/current_space`);
            const d = await res.json();
            if (d.success && d.value) {
                set((state) => {
                    const updated = { ...state.user, currentSpace: d.value };
                    const saved = localStorage.getItem('currentUser');
                    if (saved) {
                        try {
                            localStorage.setItem('currentUser', JSON.stringify({ ...JSON.parse(saved), currentSpace: d.value }));
                        } catch { /* localStorage corrupto -- no bloquear el estado en memoria por esto */ }
                    }
                    return { user: updated };
                });
            }
        } catch (err) {
            console.error("Error loading custom current_space", err);
        } finally {
            set({ spaceCheckDone: true });
        }
    },

    // Reemplaza los 2 `useEffect` de montaje de UserContext.jsx (carga de localStorage +
    // resolución de current_space) -- llamado una sola vez, desde `main.jsx` (función plana,
    // no un componente montado; ver 03.1's §1 sobre por qué NO es un `<AuthBootstrap/>` con
    // `useEffect`: ese patrón puede doble-invocar bajo StrictMode en dev, la misma clase de
    // bug que este mismo plan acaba de arreglar en DoctexHtml.jsx justo antes de esta fase).
    //
    // Orden deliberado (distinto al orden "accidental" de hoy, donde el efecto de
    // current_space corre antes de que el efecto de montaje siquiera lea localStorage en el
    // primer render): carga localStorage completo, resuelve dbId, RECIÉN DESPUÉS resuelve
    // current_space, y solo entonces marca `spaceCheckDone: true`.
    bootstrap: async () => {
        const savedUser = localStorage.getItem('currentUser');
        if (!savedUser) {
            set({ loading: false, spaceCheckDone: true });
            return;
        }

        let parsedUser;
        try {
            parsedUser = JSON.parse(savedUser);
        } catch (e) {
            console.error("Failed to parse saved user", e);
            localStorage.removeItem('currentUser');
            set({ loading: false, spaceCheckDone: true });
            return;
        }

        set((state) => ({
            user: {
                ...state.user,
                name: parsedUser.nick,
                email: parsedUser.email,
                image: parsedUser['link-profile-img'] || `https://ui-avatars.com/api/?name=${encodeURIComponent(parsedUser.nick)}&background=random&size=128`,
                isDbConnected: true,
                status: parsedUser.status || "Online",
                dbId: parsedUser.id,
                id: parsedUser.id, // Ensure 'id' is available for consumers like CommentArea
                calendar_personal: parsedUser.calendar_personal, // User's Personal Calendar ID
                spaces: parsedUser.spaces ? (typeof parsedUser.spaces === 'string' ? JSON.parse(parsedUser.spaces) : parsedUser.spaces) : null,
                currentSpace: parsedUser.currentSpace || null,
                description: parsedUser.description,
                location: parsedUser.location,
                religion: parsedUser.religion,
                tags: parsedUser.tags,
                map_pos_x: parsedUser.map_pos_x,
                map_pos_y: parsedUser.map_pos_y,
                role: parsedUser.role,
                timezone: parsedUser.timezone,
                language: localStorage.getItem('appLanguage') || parsedUser.language || 'es',
            },
            loading: false,
        }));
        i18n.changeLanguage(localStorage.getItem('appLanguage') || parsedUser.language || 'es');

        // Force refresh of Space Data (Icon/Name)
        if (parsedUser.currentSpace?.id) {
            fetch(`/api/space/${parsedUser.currentSpace.id}`)
                .then(res => res.json())
                .then(data => {
                    if (data.success && data.space) {
                        set((state) => {
                            const updated = { ...state.user, currentSpace: { ...state.user.currentSpace, ...data.space } };
                            localStorage.setItem('currentUser', JSON.stringify(updated));
                            return { user: updated };
                        });
                    }
                })
                .catch(e => console.error("Space refresh fail", e));
        }

        // Renueva la sesión si tiene 1 día o más de emitida (no-op barato si es más nueva).
        fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' })
            .catch(e => console.warn('Session refresh failed', e));

        // Force sync user data from DB (Description, etc.)
        if (parsedUser.id) {
            fetch(`/api/users/${parsedUser.id}`, { credentials: 'include' })
                .then(res => res.json())
                .then(data => {
                    if (data.success && data.user) {
                        set((state) => {
                            const updated = { ...state.user, ...data.user };
                            if (data.user.role !== undefined) updated.role = data.user.role;
                            localStorage.setItem('currentUser', JSON.stringify(updated));
                            return { user: updated };
                        });
                    }
                })
                .catch(e => console.warn("Background user sync failed", e));
        }

        await get().resolveCurrentSpace(parsedUser.id);
    },

    updateUser: async (updates) => {
        set((state) => {
            const newState = { ...state.user, ...updates };
            if (updates.status || updates.currentSpace !== undefined) {
                const saved = localStorage.getItem('currentUser');
                if (saved) {
                    try {
                        const parsed = JSON.parse(saved);
                        if (updates.status) parsed.status = updates.status;
                        if (updates.currentSpace !== undefined) parsed.currentSpace = updates.currentSpace;
                        localStorage.setItem('currentUser', JSON.stringify(parsed));
                    } catch (e) {
                        console.error("Error saving status/space to localStorage", e);
                    }
                }
            }
            if (updates.currentSpace && state.user.dbId) {
                fetch('/api/customs', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userId: state.user.dbId,
                        key: 'current_space',
                        value: updates.currentSpace
                    })
                }).catch(err => console.error("Error saving current_space custom:", err));
            }
            return { user: newState };
        });
    },

    updateUserProfile: async (updates) => {
        const currentUser = get().user;
        if (!currentUser.dbId) {
            toastSink.error("Error de sesión: no se detecta identidad. Recarga la página o cierra sesión y vuelve a entrar.");
            console.error("Missing User ID", currentUser);
            return { success: false, message: "No session ID" };
        }

        try {
            const payload = {};
            if (updates.name !== undefined) payload.nick = updates.name;
            if (updates.email !== undefined) payload.email = updates.email;
            if (updates.image) payload["link-profile-img"] = updates.image;
            if (updates.image_thumb) payload["link-profile-img-thumb"] = updates.image_thumb;
            if (updates.description !== undefined) payload.description = updates.description;
            if (updates.location !== undefined) payload.location = updates.location;
            if (updates.religion !== undefined) payload.religion = updates.religion;
            if (updates.tags !== undefined) payload.tags = updates.tags;
            if (updates.map_avatar !== undefined) payload.map_avatar = updates.map_avatar;
            if (updates.avatar_frames !== undefined) payload.avatar_frames = updates.avatar_frames;
            if (updates.avatar_description !== undefined) payload.avatar_description = updates.avatar_description;
            if (updates.avatar_char_id !== undefined) payload.avatar_char_id = updates.avatar_char_id;

            const response = await fetch(`/api/users/${currentUser.dbId}`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (data.success) {
                const dbUser = data.user;
                localStorage.setItem('currentUser', JSON.stringify(dbUser));
                set((state) => ({
                    user: {
                        ...state.user,
                        name: dbUser.nick,
                        email: dbUser.email,
                        image: dbUser['link-profile-img'] || `https://ui-avatars.com/api/?name=${encodeURIComponent(dbUser.nick)}&background=random&size=128`,
                        description: dbUser.description,
                        location: dbUser.location,
                        religion: dbUser.religion,
                        tags: dbUser.tags,
                        map_avatar: dbUser.map_avatar !== undefined ? dbUser.map_avatar : state.user.map_avatar,
                        avatar_frames: dbUser.avatar_frames || state.user.avatar_frames,
                        avatar_description: dbUser.avatar_description || state.user.avatar_description,
                        avatar_char_id: dbUser.avatar_char_id !== undefined ? dbUser.avatar_char_id : state.user.avatar_char_id,
                    },
                }));
                return { success: true };
            }
            return { success: false, message: data.message };
        } catch (error) {
            console.error("Update profile error:", error);
            throw error;
        }
    },

    // Endpoint dedicado (`PUT /api/users/me/credentials`), no `updateUserProfile` -- el backend
    // exige `currentPassword` y valida el hash real antes de tocar nick/password.
    changeCredentials: async ({ currentPassword, nick, password }) => {
        const currentUser = get().user;
        if (!currentUser.dbId) return { success: false, message: 'No session ID' };

        try {
            const response = await fetch('/api/users/me/credentials', {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ currentPassword, nick, password }),
            });
            const data = await response.json();

            if (data.success) {
                const dbUser = data.user;
                localStorage.setItem('currentUser', JSON.stringify(dbUser));
                set((state) => ({ user: { ...state.user, name: dbUser.nick } }));
                return { success: true };
            }
            return { success: false, code: data.code };
        } catch (error) {
            console.error('Change credentials error:', error);
            return { success: false, message: 'Error de conexión' };
        }
    },

    uploadProfileImage: async (file) => {
        const currentUser = get().user;
        if (!currentUser.dbId) return null;

        try {
            const url = await uploadFile(file, 'profile');
            const updateRes = await get().updateUserProfile({ image: url });
            if (!updateRes || updateRes.success === false) {
                throw new Error("Failed to save profile image link to database: " + (updateRes?.message || "Unknown error"));
            }
            return { url };
        } catch (error) {
            console.error("Error uploading image:", error);
            throw error;
        }
    },

    login: async (nick, password) => {
        try {
            const response = await fetch('/api/login', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ nick, password }),
            });

            const data = await response.json();

            if (data.success) {
                const dbUser = data.user;

                // `welcome` es TEXT en la DB -- en una instalación recién migrada llega como
                // string ("0"/"1"), no como número (ver Login.js backend, mismo bug).
                if (Number(dbUser.welcome) === 0) {
                    return { success: true, isNewUser: true, user: dbUser };
                }

                await finalizeLogin(data);
                return { success: true, isNewUser: false };
            }
            console.warn("Login failed according to server:", data.code, data.error);
            return { success: false, code: data.code };
        } catch (error) {
            console.error("Login error in detailed catch:", error);
            throw error;
        }
    },

    checkNickAvailability: async (nick) => {
        try {
            const currentUser = get().user;
            const userIdToSend = currentUser.dbId ? parseInt(currentUser.dbId) : -1;

            const response = await fetch('/api/validate-nick', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ nick, userId: userIdToSend })
            });
            return await response.json();
        } catch (error) {
            console.error("Check nick error:", error);
            return { success: false, valid: false, message: 'Check failed' };
        }
    },

    clearSession: () => {
        localStorage.removeItem('currentUser');
        set({ user: LOGGED_OUT_USER });
    },

    logout: async () => {
        // Revoca la sesión del lado del servidor antes de limpiar el estado local -- si solo
        // se limpiara localStorage, la cookie y la sesión seguirían siendo válidas contra el
        // backend.
        try {
            await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
        } catch (e) {
            console.error('Logout request failed:', e);
        }
        get().clearSession();
    },

    updateCurrentSpace: (updates) => {
        set((state) => {
            const newSpace = { ...state.user.currentSpace, ...updates };
            const saved = localStorage.getItem('currentUser');
            if (saved) {
                try {
                    const parsed = JSON.parse(saved);
                    parsed.currentSpace = newSpace;
                    localStorage.setItem('currentUser', JSON.stringify(parsed));
                } catch (e) {
                    console.error('Error saving currentSpace to localStorage', e);
                }
            }
            return { user: { ...state.user, currentSpace: newSpace } };
        });
    },
}));

// `finalizeLogin`/`clearSession` eran `useCallback`s privados de `UserContext.jsx`, cerrados
// sobre por `login`/`logout` -- acá `clearSession` vive como acción del store (la necesita el
// listener de session-expired de más abajo), pero `finalizeLogin` es un helper de módulo, no
// una acción pública, porque solo `login` la usa.
async function finalizeLogin(data) {
    const dbUser = data.user;
    if (data.currentSpace) {
        dbUser.currentSpace = data.currentSpace;
    }
    localStorage.setItem('currentUser', JSON.stringify(dbUser));

    useAuthStore.setState((state) => ({
        user: {
            ...state.user,
            name: dbUser.nick,
            email: dbUser.email,
            image: dbUser['link-profile-img'] || `https://ui-avatars.com/api/?name=${encodeURIComponent(dbUser.nick)}&background=random&size=128`,
            isDbConnected: true,
            status: "Online",
            dbId: dbUser.id,
            id: dbUser.id,
            calendar_personal: dbUser.calendar_personal,
            spaces: dbUser.spaces ? (typeof dbUser.spaces === 'string' ? JSON.parse(dbUser.spaces) : dbUser.spaces) : null,
            currentSpace: dbUser.currentSpace || null,
            map_pos_x: dbUser.map_pos_x,
            map_pos_y: dbUser.map_pos_y,
            role: dbUser.role,
            map_avatar: dbUser.map_avatar || 'default',
            avatar_char_id: dbUser.avatar_char_id !== undefined ? dbUser.avatar_char_id : null,
            avatar_frames: dbUser.avatar_frames || null,
            avatar_description: dbUser.avatar_description || '',
            timezone: dbUser.timezone,
            language: dbUser.language || 'es',
        },
    }));
    // i18n (2026-07-12): el login ya manda el idioma persistido del usuario -- se fija acá
    // mismo para que la UI arranque en el idioma correcto desde el primer render.
    localStorage.setItem('appLanguage', dbUser.language || 'es');
    i18n.changeLanguage(dbUser.language || 'es');

    // 03.1-auth-store.md §1 -- re-dispara la resolución de current_space tras un login fresco
    // (no solo en bootstrap()), para no perder un espacio persistido de otra sesión cuando el
    // login mismo no trae uno.
    await useAuthStore.getState().resolveCurrentSpace(dbUser.id);
}

// Favicon sincronizado con el ícono del space activo -- antes un `useEffect` con
// `[user?.currentSpace?.icon]` como dependencia; acá, un `subscribe()` a nivel de módulo
// (primer uso de este patrón en el repo, ver 03.1-auth-store.md §1 -- los otros 2 `.subscribe()`
// existentes están dentro de un `useEffect` con cleanup, este no tiene ciclo de vida de
// componente al cual atarse).
let lastFaviconIcon;
function syncFavicon(icon) {
    if (!icon || icon === lastFaviconIcon) return;
    lastFaviconIcon = icon;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.removeAttribute('type');
    link.href = icon;
}
if (typeof window !== 'undefined') {
    useAuthStore.subscribe((state) => {
        syncFavicon(state.user?.currentSpace?.icon);
    });
}

registerSessionExpiredListenerOnce();
